import React, { useState } from 'react';
import { format } from 'date-fns';
import { StatusBadge } from '../common';

/**
 * Unified component to display a player's recent form as W/L indicators
 * 
 * @param {Object} props
 * @param {Array} props.recentGames - Array of game objects with full data
 * @param {Array} props.results - Simple array of 'W'/'L' strings (alternative to recentGames)
 * @param {string} props.variant - Display variant ('default', 'compact', 'minimal')
 * @param {boolean} props.showLatestIndicator - Whether to show "Latest" arrow on the most recent game
 * @param {boolean} props.showLegend - Whether to show the Win/Loss legend
 * @param {boolean} props.showSummary - Whether to show the games summary text
 * @param {string} props.title - Custom title (defaults to "Recent Form")
 * @param {number} props.maxGames - Maximum number of games to display
 * @param {boolean} props.showTooltip - Whether to show detailed tooltips on hover
 */
const RecentForm = ({ 
  recentGames = [],
  results = [],
  variant = 'default',
  showLatestIndicator = true,
  showLegend = true,
  showSummary = true,
  title = "Recent Form",
  maxGames = 5,
  showTooltip = true
}) => {
  const [hoveredGame, setHoveredGame] = useState(null);
  
  // Define styling variants
  const variants = {
    default: {
      container: "bg-surface border border-border-medium rounded-lg shadow-brand-md p-6 mb-8",
      title: "text-xl font-bold mb-4 text-text-primary",
      indicator: {
        container: "w-12 h-12 rounded-lg font-bold text-lg shadow-sm",
        win: "bg-success text-text-inverse",
        loss: "bg-danger text-text-inverse"
      }
    },
    compact: {
      container: "bg-surface-subtle border border-border rounded-lg p-4",
      title: "font-medium text-text-secondary mb-2",
      indicator: {
        container: "w-8 h-8 rounded-full flex items-center justify-center text-text-inverse font-bold",
        win: "bg-success text-text-inverse",
        loss: "bg-danger text-text-inverse"
      }
    },
    minimal: {
      container: "bg-surface-subtle border border-border rounded-lg p-4",
      title: "font-medium text-text-secondary mb-3",
      indicator: {
        // Larger pills for better use of horizontal space
        container: "px-4 py-2 rounded-full text-base font-semibold",
        win: "bg-success-light text-success-dark",
        loss: "bg-danger-light text-danger-dark"
      }
    }
  };
  
  // Get the correct styling based on variant
  const styles = variants[variant] || variants.default;

  // Determine if we have data to display
  const hasGames = recentGames && recentGames.length > 0;
  const hasResults = results && results.length > 0;
  
  if (!hasGames && !hasResults) {
    return (
      <div className={`flex justify-center items-center h-24 ${styles.container}`}>
        <p className="text-text-muted">No recent games available.</p>
      </div>
    );
  }

  // Format player name with ELO and gender-based styling
  const formatPlayerDisplay = (player) => {
    if (!player) return { name: 'Unknown', className: '' };
    
    // Determine if player is female for styling
    const isFemale = player.gender?.toLowerCase() === 'female';
    
    // Format name with ELO if available
    const name = player.elo !== null && player.elo !== undefined
      ? `${player.first_name} ${player.last_name} (${Math.round(player.elo)})`
      : `${player.first_name} ${player.last_name}`;
    
    const className = isFemale ? 'text-female' : '';
    
    return { name, className };
  };

  // Determine which data source to use and process it
  let gameItems = [];
  
  if (hasGames) {
    // Process full game objects
    const processedGames = recentGames.map((game, index) => {
      // Check if we have a direct result from the database
      const hasDirectResult = game.result !== null;
      
      // If we have a direct result, use it
      if (hasDirectResult) {
        return {
          id: game.id || `game-${index}`,
          date: game.created_at ? format(new Date(game.created_at), 'MMM d') : 'Unknown',
          result: game.result === 'win' ? 'W' : 'L',
          score: game.session_games ? `${game.session_games.team_1_score}-${game.session_games.team_2_score}` : 'N/A',
          teamNumber: game.team_number,
          pairType: game.pair_type,
          teammates: game.teammates || [],
          opponents: game.opponents || [],
          teamElo: game.teamElo || 1200,
          opponentElo: game.opponentElo || 1200,
          playerElo: game.playerElo || 1200,
          rawGame: game
        };
      }
      
      // Otherwise, calculate the result based on scores and team number
      const isTeam1 = game.team_number === 1;
      const team1Score = game.session_games?.team_1_score || 0;
      const team2Score = game.session_games?.team_2_score || 0;
      
      // Determine if the player won
      const isWin = isTeam1 
        ? team1Score > team2Score 
        : team2Score > team1Score;
      
      return {
        id: game.id || `game-${index}`,
        date: game.created_at ? format(new Date(game.created_at), 'MMM d') : 'Unknown',
        result: isWin ? 'W' : 'L',
        score: `${team1Score}-${team2Score}`,
        teamNumber: game.team_number,
        pairType: game.pair_type,
        teammates: game.teammates || [],
        opponents: game.opponents || [],
        teamElo: game.teamElo || 1200,
        opponentElo: game.opponentElo || 1200,
        playerElo: game.playerElo || 1200,
        rawGame: game
      };
    });
    
    gameItems = processedGames;
  } else if (hasResults) {
    // Process simple results array (index 0 = most recent)
    gameItems = results.map((result, index) => {
      const r = String(result).toLowerCase();
      const normalized = r === 'win' || r === 'w' ? 'W' : 'L';
      return {
      id: `result-${index}`,
      result: normalized,
      // Minimal data for simple display
      date: 'Unknown',
      score: 'N/A',
      simple: true,
    };
    });
  }
  
  // Newest-first: latest game on the left (maxGames most recent)
  if (hasGames) {
    gameItems = [...gameItems]
      .sort((a, b) => {
        const ta = new Date(a.rawGame?.created_at || a.date).getTime();
        const tb = new Date(b.rawGame?.created_at || b.date).getTime();
        if (Number.isNaN(ta) || Number.isNaN(tb)) return 0;
        return tb - ta;
      })
      .slice(0, maxGames);
  } else {
    gameItems = gameItems.slice(0, maxGames);
  }

  const displayGames = gameItems;

  // Game tooltip component
  const GameTooltip = ({ game }) => {
    if (!game) return null;
    
    const isTeam1 = game.teamNumber === 1;
    const scores = game.score.split('-');
    const myTeamScore = isTeam1 ? scores[0] : scores[1];
    const opponentScore = isTeam1 ? scores[1] : scores[0];
    
    // Format player ELO display
    const playerEloDisplay = game.playerElo !== null && game.playerElo !== undefined
      ? ` (${Math.round(game.playerElo)})`
      : '';
    
    return (
      <div className="absolute z-10 w-64 p-4 bg-surface rounded-lg shadow-brand-lg border border-border-medium text-left text-text-primary">
        <div className="text-sm font-bold mb-2">{game.date}</div>
        
        <div className="mb-3">
          <div className={`text-xs font-medium ${game.result === 'W' ? 'text-success' : 'text-danger'}`}>
            {game.result === 'W' ? 'WIN' : 'LOSS'} • {game.pairType || 'Unknown'} Doubles
          </div>
          <div className="text-lg font-bold">
            <span className={game.result === 'W' ? 'text-success' : 'text-text-secondary'}>
              {myTeamScore}
            </span>
            <span className="text-text-muted mx-1">-</span>
            <span className={game.result === 'L' ? 'text-danger' : 'text-text-secondary'}>
              {opponentScore}
            </span>
          </div>
        </div>
        
        <div className="mb-2">
          <div className="text-xs text-text-muted mb-1">
            Your Team <span className="font-medium">({game.teamElo})</span>
          </div>
          <div className="flex flex-col">
            <div className="text-sm font-medium text-text-primary">
              You{playerEloDisplay}
            </div>
            {game.teammates && game.teammates.length > 0 ? (
              game.teammates.map((teammate, idx) => {
                const { name, className } = formatPlayerDisplay(teammate);
                return (
                  <div key={idx} className={`text-sm ${className}`}>
                    {name}
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-text-muted">No teammates</div>
            )}
          </div>
        </div>
        
        <div>
          <div className="text-xs text-text-muted mb-1">
            Opponents <span className="font-medium">({game.opponentElo})</span>
          </div>
          <div className="flex flex-col">
            {game.opponents && game.opponents.length > 0 ? (
              game.opponents.map((opponent, idx) => {
                const { name, className } = formatPlayerDisplay(opponent);
                return (
                  <div key={idx} className={`text-sm ${className}`}>
                    {name}
                  </div>
                );
              })
            ) : (
              <div className="text-sm text-text-muted">No opponents data</div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      {title && <h2 className={styles.title}>{title}</h2>}
      
      <div className="flex flex-col space-y-4">
        {/* Game result indicators */}
        <div className="flex justify-start items-center pt-6">
          <div className="flex flex-wrap gap-2">
            {displayGames.map((game, index) => (
              <div 
                key={game.id}
                className="relative"
                onMouseEnter={() => showTooltip && setHoveredGame(game)}
                onMouseLeave={() => showTooltip && setHoveredGame(null)}
              >
                {showLatestIndicator && index === 0 && (
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                    <div className="text-xs text-text-muted font-medium">Latest</div>
                    <div className="w-0 h-0 border-l-2 border-r-2 border-b-2 border-transparent border-b-border-medium mx-auto"></div>
                  </div>
                )}
                {(game.result === 'W' || game.result?.toLowerCase() === 'win') ? (
                  <span className="inline-flex items-center font-medium rounded-full px-2 py-1 text-xs bg-success text-text-inverse transition-transform hover:scale-105 cursor-default">
                    {game.result?.toLowerCase() === 'win' ? 'W' : game.result}
                  </span>
                ) : (
                <StatusBadge 
                    variant="danger" 
                  size="small"
                  className="transition-transform hover:scale-105 cursor-default"
                >
                    {game.result?.toLowerCase() === 'loss' ? 'L' : game.result}
                </StatusBadge>
                )}
                {showTooltip && hoveredGame && hoveredGame.id === game.id && !game.simple && (
                  <GameTooltip game={hoveredGame} />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Legend */}
        {showLegend && (
          <div className="flex justify-start items-center space-x-4 text-sm text-text-secondary">
            <div className="flex items-center">
              <div className={`w-4 h-4 ${variant === 'minimal' ? 'bg-success-light' : 'bg-success'} rounded-sm mr-1`}></div>
              <span>Win</span>
            </div>
            <div className="flex items-center">
              <div className={`w-4 h-4 ${variant === 'minimal' ? 'bg-danger-light' : 'bg-danger'} rounded-sm mr-1`}></div>
              <span>Loss</span>
            </div>
          </div>
        )}
        
        {/* Summary */}
        {showSummary && (
          <div className="text-sm text-text-secondary">
            <p>
              Last {displayGames.length} games: {' '}
              <span className="font-medium">
                {displayGames.filter(g => g.result === 'W').length} wins, {' '}
                {displayGames.filter(g => g.result === 'L').length} losses
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentForm; 